import { CheckCircle } from '@mui/icons-material';
import { Button } from 'finara-components';
import { useState } from 'react';

import { paymentOtpService } from '@/services/paymentOtp.service';
import { transactionsService } from '@/services/transactions.service';
import type { ParsedPaymentRow, PaymentRequest, PaymentWizardState } from '@/types/payment';
import type { BulkPaymentDto, BulkPaymentItem } from '@/types/transactions';

interface OTPVerificationProps {
  onVerify: (paymentRequest: PaymentRequest) => void;
  wizardState: PaymentWizardState;
}

export const OTPVerification: React.FC<OTPVerificationProps> = ({ onVerify, wizardState }) => {
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleOtpChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(numericValue);
    setError('');
  };

  const handleConfirmPayment = async () => {
    if (otpCode.length !== 6) {
      setError('El código OTP debe tener 6 dígitos');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Step 1: Verify OTP with Supabase (frontend only)
      const otpResult = await paymentOtpService.verifyPaymentOtp(otpCode);

      if (!otpResult.valid) {
        setError(otpResult.error || 'Código OTP incorrecto');
        setIsVerifying(false);
        return;
      }

      // Step 2: Process payment based on type
      if (wizardState.paymentType === 'bulk') {
        // Process bulk payment
        await processBulkPayment();
      } else {
        // Process single payment
        await processSinglePayment();
      }
    } catch (err) {
      console.error('Error en el pago:', err);
      setError(
        err instanceof Error ? err.message : 'Error al procesar el pago. Intenta nuevamente.'
      );
      setIsVerifying(false);
    }
  };

  const processSinglePayment = async () => {
    if (!wizardState.source || !wizardState.data || !wizardState.summary) {
      throw new Error('Datos de pago incompletos');
    }

    try {
      const sourceAccount = wizardState.source.selectedAccount;
      const { beneficiary, amount, concept, executionType, scheduledDate, scheduledTime } =
        wizardState.data;

      // If payment is scheduled, use bulk endpoint with single payment
      if (executionType === 'scheduled' && scheduledDate && scheduledTime) {
        // Format scheduled date to ISO format
        const scheduledDateTimeStr = `${scheduledDate} ${scheduledTime}`;

        // Generate idempotency key
        const idempotencyKey = `single-${Date.now()}-${Math.random().toString(36).substring(7)}`;

        const bulkDto: BulkPaymentDto = {
          sourceAccountId: sourceAccount.id,
          idempotencyKey,
          payments: [
            {
              scheduledDate: scheduledDateTimeStr,
              beneficiary: {
                clabe: beneficiary.clabe,
                name: beneficiary.fullName,
                rfc: beneficiary.rfc || undefined,
                email: beneficiary.email || undefined,
              },
              amount,
              concept,
            },
          ],
        };

        const response = await transactionsService.bulkPayment(bulkDto);

        // Create payment request object for success screen
        const paymentRequest: PaymentRequest = {
          id: idempotencyKey,
          type: 'singular',
          source: wizardState.source,
          data: wizardState.data,
          summary: wizardState.summary,
          status: response.summary.scheduled > 0 ? 'pending' : 'completed',
          operationReference:
            response.results.scheduled[0]?.scheduledTransactionId || idempotencyKey,
          processedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        onVerify(paymentRequest);
        return;
      }

      // For immediate payments, use the admin transfer endpoint
      const transaction = await transactionsService.adminTransfer({
        sourceAccountId: sourceAccount.id,
        beneficiaryClabe: beneficiary.clabe,
        beneficiaryName: beneficiary.fullName,
        beneficiaryRfc: beneficiary.rfc || undefined,
        amount,
        concept,
        currency: 'MXN',
      });

      // Create payment request object for success screen
      const paymentRequest: PaymentRequest = {
        id: transaction.id,
        type: 'singular',
        source: wizardState.source,
        data: wizardState.data,
        summary: wizardState.summary,
        status: 'completed',
        operationReference: transaction.reference,
        processedAt: transaction.createdAt,
        createdAt: transaction.createdAt,
        updatedAt: transaction.updatedAt,
      };

      onVerify(paymentRequest);
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Error al procesar el pago individual');
    }
  };

  const processBulkPayment = async () => {
    if (!wizardState.source || !wizardState.bulkValidation) {
      throw new Error('Datos de pago masivo incompletos');
    }

    try {
      const sourceAccount = wizardState.source.selectedAccount;
      const validPayments = wizardState.bulkValidation.payments.filter(
        (p: ParsedPaymentRow) => p.isValid
      );

      // Transform payments to backend format
      const payments: BulkPaymentItem[] = validPayments.map((payment: ParsedPaymentRow) => ({
        scheduledDate: payment.fecha || undefined,
        beneficiary: {
          clabe: payment.clabe,
          name: payment.beneficiario,
          rfc: payment.rfc || undefined,
          email: payment.email || undefined,
        },
        amount: payment.monto,
        concept: payment.concepto,
      }));

      // Generate idempotency key
      const idempotencyKey = `bulk-${Date.now()}-${Math.random().toString(36).substring(7)}`;

      const bulkDto: BulkPaymentDto = {
        sourceAccountId: sourceAccount.id,
        idempotencyKey,
        payments,
      };

      const response = await transactionsService.bulkPayment(bulkDto);

      // Store results in wizard state and move to bulk-processing step
      // The wizard will handle the transition to bulk-processing
      const paymentRequest: PaymentRequest = {
        id: idempotencyKey,
        type: 'bulk',
        source: wizardState.source,
        data: {} as PaymentRequest['data'], // Bulk doesn't use single data
        summary: {
          paymentAmount: response.summary.totalAmount,
          total: response.summary.totalAmount,
        },
        status: 'completed',
        operationReference: idempotencyKey,
        processedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Store bulk results in wizard state for BulkProcessing/BulkResults
      wizardState.bulkResponse = response;

      onVerify(paymentRequest);
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Error al procesar pagos masivos');
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-brand-blue" fontSize="large" />
        </div>
        <h2 className="text-2xl font-bold text-brand-white mb-2">Autorización OTP</h2>
        <p className="text-muted">
          Para completar el pago, ingresa el código de tu autenticador
        </p>
      </div>

      {/* OTP Status */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <CheckCircle className="text-brand-blue mr-2" fontSize="small" />
          <span className="text-sm text-muted">
            Abre tu aplicación autenticadora y genera un código de 6 dígitos
          </span>
        </div>
      </div>

      {/* OTP Input */}
      <div className="mb-6">
        <label htmlFor="otp-code" className="block text-sm font-medium text-brand-white mb-3">
          Código OTP (6 dígitos)
        </label>
        <input
          id="otp-code"
          type="text"
          value={otpCode}
          onChange={e => handleOtpChange(e.target.value)}
          placeholder="000000"
          className={`w-full px-4 py-3 text-center text-2xl font-mono border rounded-lg bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
            error ? 'border-red-500' : 'border-border'
          }`}
          maxLength={6}
          disabled={isVerifying}
        />
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      </div>

      {/* Navigation */}
      <div className="space-y-3">
        <Button
          variant="black"
          size="large"
          fullWidth
          onClick={() => void handleConfirmPayment()}
          disabled={otpCode.length !== 6 || isVerifying}
        >
          {isVerifying ? 'Procesando...' : 'Confirmar Pago'}
        </Button>
      </div>
    </div>
  );
};
